package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.model.capabilities
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.cost_rule_added
import evoshift.composeapp.generated.resources.cost_rule_deleted
import evoshift.composeapp.generated.resources.cost_rule_updated
import evoshift.composeapp.generated.resources.cost_rules_one_off_moved
import com.evoapps.evoshift.domain.usecase.finance.MigrateOneOffCostRulesUseCase
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.CostFrequency
import com.evoapps.evoshift.domain.model.RecurringCostRule
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.settings.CreateRecurringCostRuleUseCase
import com.evoapps.evoshift.domain.model.LedgerKind
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.domain.usecase.settings.DeleteRecurringCostRuleUseCase
import com.evoapps.evoshift.domain.usecase.settings.GetRecurringCostRulesUseCase
import com.evoapps.evoshift.domain.usecase.settings.UpdateRecurringCostRuleUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase

data class WorkplaceInfo(
    val id: String,
    val name: String,
    val currency: String
)

data class RecurringCostRulesUiState(
    val isLoading: Boolean = false,
    val rules: List<RecurringCostRule> = emptyList(),
    /** The workplaces the person owns (MANAGE_COST_RULES): the targets a rule may have. */
    val workplaces: Map<String, WorkplaceInfo> = emptyMap(), // id to info
    val error: UiText? = null,
    val successMessage: UiText? = null
)

sealed interface RecurringCostRulesEvent {
    data class AddRule(
        val name: String,
        val amount: Double,
        val frequency: CostFrequency,
        val targetWorkplaceId: String,
        /** SH-87: a cost or revenue. */
        val kind: LedgerKind = LedgerKind.COST
    ) : RecurringCostRulesEvent
    
    data class UpdateRule(val rule: RecurringCostRule) : RecurringCostRulesEvent
    data class DeleteRule(val ruleId: String) : RecurringCostRulesEvent
    data object MessageShown : RecurringCostRulesEvent
    data object Refresh : RecurringCostRulesEvent
}

class RecurringCostRulesViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getWorkplaceUseCase: GetWorkplaceUseCase,
    private val getRecurringCostRulesUseCase: GetRecurringCostRulesUseCase,
    private val createRecurringCostRuleUseCase: CreateRecurringCostRuleUseCase,
    private val updateRecurringCostRuleUseCase: UpdateRecurringCostRuleUseCase,
    private val deleteRecurringCostRuleUseCase: DeleteRecurringCostRuleUseCase,
    /** Moves old "one-off" rules into their month's costs on the first load (SH-84). */
    private val migrateOneOffCostRulesUseCase: MigrateOneOffCostRulesUseCase? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(RecurringCostRulesUiState())
    val uiState: StateFlow<RecurringCostRulesUiState> = _uiState.asStateFlow()

    init {
        loadData()
    }

    fun onEvent(event: RecurringCostRulesEvent) {
        when (event) {
            is RecurringCostRulesEvent.AddRule -> addRule(event.name, event.amount, event.frequency, event.targetWorkplaceId, event.kind)
            is RecurringCostRulesEvent.UpdateRule -> updateRule(event.rule)
            is RecurringCostRulesEvent.DeleteRule -> deleteRule(event.ruleId)
            RecurringCostRulesEvent.MessageShown -> _uiState.update { it.copy(error = null, successMessage = null) }
            RecurringCostRulesEvent.Refresh -> loadData()
        }
    }

    /** Full load: workplaces and rules. Only shows the spinner while there are no rules on screen. */
    private fun loadData() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = it.rules.isEmpty()) }
            val user = getCurrentUserUseCase().first() ?: return@launch
            
            val workplaces = mutableMapOf<String, WorkplaceInfo>()
            // The owner's own rules, for the workplaces they own; nobody else has any to read.
            user.engagements.values
                .filter { it.capabilities.shows(Gate.RECURRING_COSTS) }
                .forEach { engagement ->
                    // Fetch each workplace to get the latest currency
                    getWorkplaceUseCase(engagement.workplaceId).onSuccess { wp ->
                        // A co-owner's OWNER engagement is not the workplace's owner: the rules
                        // live under the owner's own document (owners/{ownerId}).
                        if (wp.ownerId != user.uid) return@onSuccess
                        workplaces[wp.id] = WorkplaceInfo(
                            id = wp.id,
                            name = wp.name,
                            currency = wp.settings.currency
                        )
                    }
                }
            
            _uiState.update { it.copy(workplaces = workplaces) }
            if (workplaces.isEmpty()) {
                _uiState.update { it.copy(isLoading = false, rules = emptyList()) }
                return@launch
            }

            getRecurringCostRulesUseCase(user.uid).fold(
                onSuccess = { rules ->
                    _uiState.update { it.copy(isLoading = false, rules = rules) }
                    migrateOneOffRules(user.uid, rules)
                },
                onFailure = { e ->
                    _uiState.update { it.copy(isLoading = false, error = e.toUiText()) }
                }
            )
        }
    }

    private fun addRule(name: String, amount: Double, frequency: CostFrequency, targetWorkplaceId: String, kind: LedgerKind = LedgerKind.COST) {
        val before = _uiState.value.rules
        val workplace = _uiState.value.workplaces[targetWorkplaceId]
        val newRule = RecurringCostRule(
            id = "", // Backend sets it
            name = name,
            amount = amount,
            currency = workplace?.currency ?: "PLN",
            frequency = frequency,
            targetWorkplaceId = targetWorkplaceId,
            kind = kind
        )
        // A placeholder id until the re-read brings the one Firestore generated.
        val pendingId = "$PENDING_ID_PREFIX${before.size}-$name"
        _uiState.update { it.copy(rules = before + newRule.copy(id = pendingId)) }

        viewModelScope.launch {
            val user = getCurrentUserUseCase().first() ?: return@launch rollBack(before, null)
            createRecurringCostRuleUseCase(user.uid, newRule).fold(
                onSuccess = {
                    if (kind == LedgerKind.REVENUE) AppTelemetry.log(AnalyticsEvent.RecurringRevenueAdded)
                    _uiState.update { it.copy(successMessage = UiText.Resource(Res.string.cost_rule_added)) }
                    refreshRules(user.uid)
                },
                onFailure = { e -> rollBack(before, e) }
            )
        }
    }

    private fun updateRule(rule: RecurringCostRule) {
        if (rule.id.isPending) return
        val before = _uiState.value.rules
        val workplace = _uiState.value.workplaces[rule.targetWorkplaceId]
        val updatedRule = rule.copy(currency = workplace?.currency ?: rule.currency)
        _uiState.update { state -> state.copy(rules = before.map { if (it.id == rule.id) updatedRule else it }) }

        viewModelScope.launch {
            val user = getCurrentUserUseCase().first() ?: return@launch rollBack(before, null)
            updateRecurringCostRuleUseCase(user.uid, updatedRule).fold(
                onSuccess = {
                    _uiState.update { it.copy(successMessage = UiText.Resource(Res.string.cost_rule_updated)) }
                },
                onFailure = { e -> rollBack(before, e) }
            )
        }
    }

    private fun deleteRule(ruleId: String) {
        if (ruleId.isPending) return
        val before = _uiState.value.rules
        _uiState.update { state -> state.copy(rules = before.filterNot { it.id == ruleId }) }

        viewModelScope.launch {
            val user = getCurrentUserUseCase().first() ?: return@launch rollBack(before, null)
            deleteRecurringCostRuleUseCase(user.uid, ruleId).fold(
                onSuccess = {
                    _uiState.update { it.copy(successMessage = UiText.Resource(Res.string.cost_rule_deleted)) }
                },
                onFailure = { e -> rollBack(before, e) }
            )
        }
    }

    /** Undoes an optimistic change whose save failed (or never started: no user), and says why. */
    private fun rollBack(before: List<RecurringCostRule>, e: Throwable?) {
        _uiState.update { it.copy(rules = before, error = e?.toUiText() ?: it.error) }
    }

    /**
     * SH-84: a one-off expense belongs to a month. Old rules with the frequency "one-off" move into
     * the current month's costs of their workplace (idempotent); those whose month has no roster
     * yet stay listed as they are.
     */
    private suspend fun migrateOneOffRules(ownerId: String, rules: List<RecurringCostRule>) {
        val migrate = migrateOneOffCostRulesUseCase ?: return
        if (rules.none { it.frequency == CostFrequency.ONE_OFF }) return
        val moved = migrate(ownerId, rules).getOrDefault(emptyList())
        if (moved.isEmpty()) return
        _uiState.update {
            it.copy(
                rules = it.rules.filterNot { rule -> rule.id in moved },
                successMessage = UiText.Resource(Res.string.cost_rules_one_off_moved, moved.size)
            )
        }
    }

    /** After adding: re-reads the rules without a spinner, for the id Firestore generated. */
    private suspend fun refreshRules(ownerId: String) {
        getRecurringCostRulesUseCase(ownerId).onSuccess { rules ->
            _uiState.update { it.copy(rules = rules) }
        }
    }
}
